import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Editor, { loader } from '@monaco-editor/react';
import { FiCode, FiEye, FiUpload, FiFile, FiImage, FiTrash2, FiEdit2, FiPlus, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import axios from 'axios';

// Configure Monaco loader to use jsdelivr CDN (more reliable)
loader.config({
  paths: {
    vs: 'https://cdn.jsdelivr.net/npm/monaco-editor@0.45.0/min/vs'
  }
});

function BuildExtension() {
  const navigate = useNavigate();
  const [activeFile, setActiveFile] = useState('manifest.json');
  const [previewMode, setPreviewMode] = useState('popup');
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [editingFileName, setEditingFileName] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'productivity',
    version: '1.0.0',
    tags: '',
    permissions: 'storage, tabs'
  });

  // Files state
  const [files, setFiles] = useState({
    'manifest.json': {
      content: JSON.stringify({
        name: "My Extension",
        version: "1.0.0",
        manifest_version: 2,
        description: "Extension description",
        permissions: ["storage", "tabs"],
        background: { scripts: ["background.js"], persistent: true },
        content_scripts: [{ matches: ["<all_urls>"], js: ["content.js"], run_at: "document_end" }],
        browser_action: { default_popup: "popup.html" },
        chrome_url_overrides: { newtab: "newtab.html" }
      }, null, 2),
      language: 'json',
      icon: FiFile
    },
    'background.js': {
      content: `// Background Script
chrome.runtime.onInstalled.addListener(() => {
  console.log('Extension installed!');
  chrome.storage.local.set({ installed: true });
});

chrome.runtime.onMessage.addListener((request, sender, sendResponse) => {
  if (request.action === 'getData') {
    chrome.storage.local.get(['data'], (result) => {
      sendResponse({ data: result.data || 'No data' });
    });
    return true;
  }
});`,
      language: 'javascript',
      icon: FiCode
    },
    'content.js': {
      content: `// Content Script
console.log('Content script loaded on:', window.location.href);

window.addEventListener('load', () => {
  const banner = document.createElement('div');
  banner.style.cssText = \`
    position: fixed; top: 10px; right: 10px;
    background: #000; color: #fff;
    padding: 10px 20px; border-radius: 5px;
    z-index: 999999; font-family: Arial;
  \`;
  banner.textContent = 'AVALW Extension Active';
  document.body.appendChild(banner);
  setTimeout(() => banner.remove(), 3000);
});`,
      language: 'javascript',
      icon: FiCode
    },
    'popup.html': {
      content: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      width: 350px; height: 550px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      color: white; display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      text-align: center; padding: 30px;
    }
    .snake-icon {
      font-size: 80px; margin-bottom: 20px;
      animation: float 3s ease-in-out infinite;
    }
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50% { transform: translateY(-10px); }
    }
    h1 {
      font-size: 32px; margin-bottom: 10px;
      background: linear-gradient(135deg, #10b981, #3b82f6);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .subtitle {
      font-size: 16px; opacity: 0.8; margin-bottom: 30px;
      color: #94a3b8;
    }
    .stats {
      display: flex; gap: 20px; margin-bottom: 30px;
    }
    .stat {
      background: rgba(255,255,255,0.1);
      padding: 15px 20px; border-radius: 12px;
      border: 1px solid rgba(59, 130, 246, 0.3);
    }
    .stat-value {
      font-size: 24px; font-weight: bold;
      color: #3b82f6; margin-bottom: 5px;
    }
    .stat-label {
      font-size: 11px; opacity: 0.7;
    }
    button {
      width: 100%; padding: 16px;
      background: linear-gradient(135deg, #10b981, #3b82f6);
      color: white; border: none;
      border-radius: 12px; font-size: 18px;
      font-weight: 700; cursor: pointer;
      transition: all 0.3s;
      box-shadow: 0 5px 20px rgba(59, 130, 246, 0.4);
    }
    button:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 30px rgba(59, 130, 246, 0.6);
    }
    .hint {
      margin-top: 20px; font-size: 12px;
      opacity: 0.5;
    }
  </style>
</head>
<body>
  <div class="snake-icon">🐍</div>
  <h1>Snake Offline</h1>
  <p class="subtitle">Classic arcade game, offline mode</p>
  <div class="stats">
    <div class="stat">
      <div class="stat-value" id="highScore">0</div>
      <div class="stat-label">High Score</div>
    </div>
    <div class="stat">
      <div class="stat-value" id="gamesPlayed">0</div>
      <div class="stat-label">Games Played</div>
    </div>
  </div>
  <button id="playBtn">🎮 Play Game</button>
  <div class="hint">Game opens in a new tab</div>
  <script>
    // Load stats from storage
    chrome.storage.local.get(['snakeHighScore', 'gamesPlayed'], (result) => {
      document.getElementById('highScore').textContent = result.snakeHighScore || 0;
      document.getElementById('gamesPlayed').textContent = result.gamesPlayed || 0;
    });

    // Open game in new tab
    document.getElementById('playBtn').addEventListener('click', () => {
      chrome.tabs.create({ url: chrome.runtime.getURL('newtab.html') });

      // Increment games played
      chrome.storage.local.get(['gamesPlayed'], (result) => {
        const played = (result.gamesPlayed || 0) + 1;
        chrome.storage.local.set({ gamesPlayed: played });
      });
    });
  </script>
</body>
</html>`,
      language: 'html',
      icon: FiFile
    },
    'newtab.html': {
      content: `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>🐍 Snake Game</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
      color: white; min-height: 100vh;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
    }
    .game-wrapper {
      background: rgba(0,0,0,0.3);
      padding: 30px; border-radius: 20px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.5);
    }
    h1 {
      text-align: center; font-size: 36px; margin-bottom: 10px;
    }
    .score-board {
      display: flex; justify-content: center; gap: 40px;
      padding: 20px; background: rgba(59, 130, 246, 0.1);
      border-radius: 15px; margin-bottom: 20px;
    }
    .score-item {
      text-align: center;
    }
    .score-value {
      font-size: 32px; font-weight: bold; color: #3b82f6;
    }
    .score-label {
      font-size: 12px; opacity: 0.7; margin-top: 5px;
    }
    canvas {
      border: 3px solid #3b82f6;
      border-radius: 15px;
      box-shadow: 0 0 40px rgba(59, 130, 246, 0.4);
      background: #1e293b; display: block;
      margin: 0 auto 20px;
    }
    .controls {
      text-align: center; padding: 20px;
    }
    button {
      padding: 14px 35px; margin: 0 8px;
      background: #3b82f6; color: white;
      border: none; border-radius: 10px;
      font-weight: 600; cursor: pointer;
      transition: all 0.2s; font-size: 16px;
    }
    button:hover {
      background: #2563eb;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(59, 130, 246, 0.5);
    }
    button.secondary {
      background: rgba(255,255,255,0.1);
    }
    button.secondary:hover { background: rgba(255,255,255,0.2); }
    .game-over {
      position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
      background: rgba(0,0,0,0.95); padding: 40px; border-radius: 20px;
      text-align: center; display: none; border: 3px solid #ef4444;
      z-index: 10;
    }
    .game-over.show { display: block; }
    .hint {
      margin-top: 15px; font-size: 13px; opacity: 0.6;
    }
  </style>
</head>
<body>
  <div class="game-wrapper">
    <h1>🐍 Snake Game</h1>
    <div class="score-board">
      <div class="score-item">
        <div class="score-value" id="score">0</div>
        <div class="score-label">SCORE</div>
      </div>
      <div class="score-item">
        <div class="score-value" id="highScore">0</div>
        <div class="score-label">HIGH SCORE</div>
      </div>
    </div>
    <div style="position: relative;">
      <canvas id="game" width="600" height="600"></canvas>
      <div class="game-over" id="gameOver">
        <h2 style="color: #ef4444; margin-bottom: 15px; font-size: 32px;">💀 Game Over!</h2>
        <p style="font-size: 20px; margin-bottom: 25px;">Final Score: <span id="finalScore" style="color: #3b82f6; font-weight: bold;">0</span></p>
        <button onclick="resetGame()">🔄 Play Again</button>
      </div>
    </div>
    <div class="controls">
      <button id="startBtn">▶️ Start Game</button>
      <button class="secondary" onclick="pauseGame()">⏸️ Pause</button>
      <button class="secondary" onclick="resetGame()">🔄 Reset</button>
      <div class="hint">Use Arrow Keys to control the snake</div>
    </div>
  </div>
  <script>
    const canvas = document.getElementById('game');
    const ctx = canvas.getContext('2d');
    const gridSize = 30;
    const tileCount = canvas.width / gridSize;

    let snake = [{ x: 10, y: 10 }];
    let dx = 0, dy = 0;
    let food = { x: 15, y: 15 };
    let score = 0;
    let highScore = 0;
    let gameLoop;
    let isPlaying = false;

    // Load high score from storage
    if (typeof chrome !== 'undefined' && chrome.storage) {
      chrome.storage.local.get(['snakeHighScore'], (result) => {
        highScore = result.snakeHighScore || 0;
        document.getElementById('highScore').textContent = highScore;
      });
    }

    function draw() {
      // Clear canvas
      ctx.fillStyle = '#1e293b';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw grid
      ctx.strokeStyle = 'rgba(255,255,255,0.05)';
      ctx.lineWidth = 1;
      for (let i = 0; i < tileCount; i++) {
        ctx.beginPath();
        ctx.moveTo(i * gridSize, 0);
        ctx.lineTo(i * gridSize, canvas.height);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, i * gridSize);
        ctx.lineTo(canvas.width, i * gridSize);
        ctx.stroke();
      }

      // Draw snake
      snake.forEach((segment, index) => {
        const gradient = ctx.createLinearGradient(
          segment.x * gridSize, segment.y * gridSize,
          segment.x * gridSize + gridSize, segment.y * gridSize + gridSize
        );
        if (index === 0) {
          gradient.addColorStop(0, '#10b981');
          gradient.addColorStop(1, '#059669');
        } else {
          gradient.addColorStop(0, '#34d399');
          gradient.addColorStop(1, '#10b981');
        }
        ctx.fillStyle = gradient;
        ctx.fillRect(segment.x * gridSize + 2, segment.y * gridSize + 2, gridSize - 4, gridSize - 4);
        ctx.strokeStyle = '#064e3b';
        ctx.lineWidth = 2;
        ctx.strokeRect(segment.x * gridSize + 2, segment.y * gridSize + 2, gridSize - 4, gridSize - 4);
      });

      // Draw food
      const foodGradient = ctx.createRadialGradient(
        food.x * gridSize + gridSize/2, food.y * gridSize + gridSize/2, 2,
        food.x * gridSize + gridSize/2, food.y * gridSize + gridSize/2, gridSize/2
      );
      foodGradient.addColorStop(0, '#ef4444');
      foodGradient.addColorStop(1, '#dc2626');
      ctx.fillStyle = foodGradient;
      ctx.beginPath();
      ctx.arc(food.x * gridSize + gridSize/2, food.y * gridSize + gridSize/2, gridSize/2 - 3, 0, Math.PI * 2);
      ctx.fill();
    }

    function update() {
      const head = { x: snake[0].x + dx, y: snake[0].y + dy };

      // Check collisions
      if (head.x < 0 || head.x >= tileCount || head.y < 0 || head.y >= tileCount ||
          snake.some(segment => segment.x === head.x && segment.y === head.y)) {
        gameOver();
        return;
      }

      snake.unshift(head);

      // Check food collision
      if (head.x === food.x && head.y === food.y) {
        score += 10;
        document.getElementById('score').textContent = score;
        if (score > highScore) {
          highScore = score;
          document.getElementById('highScore').textContent = highScore;
          if (typeof chrome !== 'undefined' && chrome.storage) {
            chrome.storage.local.set({ snakeHighScore: highScore });
          }
        }
        placeFood();
      } else {
        snake.pop();
      }

      draw();
    }

    function placeFood() {
      do {
        food = {
          x: Math.floor(Math.random() * tileCount),
          y: Math.floor(Math.random() * tileCount)
        };
      } while (snake.some(segment => segment.x === food.x && segment.y === food.y));
    }

    function gameOver() {
      clearInterval(gameLoop);
      isPlaying = false;
      document.getElementById('finalScore').textContent = score;
      document.getElementById('gameOver').classList.add('show');
    }

    function resetGame() {
      snake = [{ x: 10, y: 10 }];
      dx = 0; dy = 0;
      score = 0;
      document.getElementById('score').textContent = 0;
      document.getElementById('gameOver').classList.remove('show');
      placeFood();
      draw();
    }

    function pauseGame() {
      if (isPlaying) {
        clearInterval(gameLoop);
        isPlaying = false;
      }
    }

    document.getElementById('startBtn').addEventListener('click', () => {
      if (!isPlaying) {
        if (dx === 0 && dy === 0) { dx = 1; dy = 0; }
        gameLoop = setInterval(update, 80);
        isPlaying = true;
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowUp' && dy === 0) { dx = 0; dy = -1; }
      if (e.key === 'ArrowDown' && dy === 0) { dx = 0; dy = 1; }
      if (e.key === 'ArrowLeft' && dx === 0) { dx = -1; dy = 0; }
      if (e.key === 'ArrowRight' && dx === 0) { dx = 1; dy = 0; }
      if (e.key === ' ') {
        e.preventDefault();
        if (isPlaying) pauseGame();
        else document.getElementById('startBtn').click();
      }
    });

    resetGame();
  </script>
</body>
</html>`,
      language: 'html',
      icon: FiFile
    },
    'styles.css': {
      content: `/* Stylesheet for content scripts */
.avalw-extension-element {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  font-family: Arial, sans-serif;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}`,
      language: 'css',
      icon: FiFile
    }
  });

  const getFileExtension = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'js') return 'javascript';
    if (ext === 'json') return 'json';
    if (ext === 'html') return 'html';
    if (ext === 'css') return 'css';
    return 'plaintext';
  };

  const handleEditorChange = (value) => {
    setFiles({
      ...files,
      [activeFile]: { ...files[activeFile], content: value }
    });
  };

  const handleDeleteFile = (filename) => {
    if (filename === 'manifest.json') {
      toast.error('Cannot delete manifest.json - it\'s required!');
      return;
    }
    const newFiles = { ...files };
    delete newFiles[filename];
    setFiles(newFiles);
    if (activeFile === filename) {
      setActiveFile('manifest.json');
    }
    toast.success(`Deleted ${filename}`);
  };

  const handleRenameFile = (oldName, newName) => {
    if (!newName || newName === oldName) {
      setEditingFileName(null);
      return;
    }
    if (files[newName]) {
      toast.error('File already exists!');
      return;
    }
    const newFiles = {};
    Object.keys(files).forEach(key => {
      if (key === oldName) {
        newFiles[newName] = { ...files[key], language: getFileExtension(newName) };
      } else {
        newFiles[key] = files[key];
      }
    });
    setFiles(newFiles);
    if (activeFile === oldName) {
      setActiveFile(newName);
    }
    setEditingFileName(null);
    toast.success(`Renamed to ${newName}`);
  };

  const handleAddFile = () => {
    const filename = prompt('Enter filename (e.g., script.js, page.html):');
    if (!filename) return;
    if (files[filename]) {
      toast.error('File already exists!');
      return;
    }
    setFiles({
      ...files,
      [filename]: {
        content: '',
        language: getFileExtension(filename),
        icon: FiFile
      }
    });
    setActiveFile(filename);
    toast.success(`Created ${filename}`);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const [buildResult, setBuildResult] = useState(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBuild = async () => {
    if (!formData.name || !formData.description) {
      toast.error('Please fill in name and description');
      return;
    }

    setIsBuilding(true);

    try {
      const token = localStorage.getItem('token');
      const uploadFormData = new FormData();

      uploadFormData.append('name', formData.name);
      uploadFormData.append('description', formData.description);
      uploadFormData.append('category', formData.category);
      uploadFormData.append('version', formData.version);
      uploadFormData.append('tags', JSON.stringify(formData.tags.split(',').map(t => t.trim()).filter(t => t)));
      uploadFormData.append('permissions', JSON.stringify(formData.permissions.split(',').map(p => p.trim()).filter(p => p)));

      Object.keys(files).forEach(filename => {
        const file = new File([files[filename].content], filename, { type: 'text/plain' });
        const key = filename.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9]/g, '_');
        uploadFormData.append(key, file);
      });

      if (logo) uploadFormData.append('logo', logo);

      const response = await axios.post('https://extension.avalw.com/api/extensions/build', uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setBuildResult(response.data.data);
      toast.success('Extension built successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Build failed');
    } finally {
      setIsBuilding(false);
    }
  };

  const handleDownload = () => {
    if (buildResult) {
      window.open(`https://extension.avalw.com${buildResult.downloadUrl}`, '_blank');
    }
  };

  const handleSubmitForApproval = async () => {
    if (!buildResult) return;

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`https://extension.avalw.com/api/extensions/${buildResult.id}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Extension submitted for approval!');
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Submit failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header */}
      <div className="bg-gray-900 text-white shadow-lg">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
            >
              <FiArrowLeft /> Back to Dashboard
            </button>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <FiCode /> Extension Builder
            </h1>
          </div>
          <button
            onClick={handleBuild}
            disabled={isBuilding}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 rounded-lg font-medium transition-colors"
          >
            <FiUpload /> {isBuilding ? 'Building...' : 'Build Extension'}
          </button>
        </div>
      </div>

      {/* Build Result Modal */}
      {buildResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <FiCode className="text-3xl text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Build Successful!</h2>
              <p className="text-gray-600 mt-2">Your extension has been compiled to .avalw format</p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Name:</span>
                <span className="font-medium text-gray-900">{buildResult.name}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Version:</span>
                <span className="font-medium text-gray-900">{buildResult.version}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Size:</span>
                <span className="font-medium text-gray-900">{(buildResult.size / 1024).toFixed(2)} KB</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-yellow-600">Draft</span>
              </div>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleDownload}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                <FiUpload className="rotate-180" /> Download .avalw File
              </button>
              <p className="text-xs text-center text-gray-500">
                Test the extension in Cronos Browser before submitting
              </p>
              <button
                onClick={handleSubmitForApproval}
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
              >
                {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
              </button>
              <button
                onClick={() => setBuildResult(null)}
                className="w-full px-6 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Continue Editing
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - File Manager + Metadata */}
        <div className="w-72 bg-white border-r border-gray-200 flex flex-col overflow-hidden shadow-lg">
          {/* File List */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-gray-900">FILES</h3>
              <button
                onClick={handleAddFile}
                className="p-1.5 hover:bg-gray-200 rounded"
                title="Add new file"
              >
                <FiPlus className="text-gray-700" />
              </button>
            </div>
            <div className="space-y-1">
              {Object.keys(files).map(filename => {
                const Icon = files[filename].icon;
                const isEditing = editingFileName === filename;
                return (
                  <div
                    key={filename}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg group ${
                      activeFile === filename ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="text-sm flex-shrink-0 text-gray-600" />
                    {isEditing ? (
                      <input
                        type="text"
                        defaultValue={filename}
                        onBlur={(e) => handleRenameFile(filename, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleRenameFile(filename, e.target.value);
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-blue-500 rounded"
                        autoFocus
                      />
                    ) : (
                      <>
                        <span
                          onClick={() => setActiveFile(filename)}
                          className="flex-1 text-sm cursor-pointer truncate text-gray-900 font-medium"
                        >
                          {filename}
                        </span>
                        <div className="hidden group-hover:flex items-center gap-1">
                          <button
                            onClick={() => setEditingFileName(filename)}
                            className="p-1 hover:bg-gray-300 rounded"
                            title="Rename"
                          >
                            <FiEdit2 className="text-xs text-gray-600" />
                          </button>
                          {filename !== 'manifest.json' && (
                            <button
                              onClick={() => handleDeleteFile(filename)}
                              className="p-1 hover:bg-red-100 rounded"
                              title="Delete"
                            >
                              <FiTrash2 className="text-xs text-red-600" />
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Metadata Form */}
          <div className="border-t border-gray-200 p-4 space-y-3 overflow-y-auto bg-gray-50">
            <h3 className="font-bold text-gray-900 mb-3">EXTENSION INFO</h3>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="My Extension"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Extension description"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="productivity">Productivity</option>
                <option value="social">Social</option>
                <option value="utilities">Utilities</option>
                <option value="entertainment">Entertainment</option>
                <option value="developer">Developer Tools</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="1.0.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Logo</label>
              <div className="flex items-center gap-2">
                {logoPreview && (
                  <img src={logoPreview} alt="Logo" className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
                )}
                <label className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm cursor-pointer hover:bg-gray-50 text-center font-medium">
                  <FiImage className="inline mr-2" />
                  {logo ? 'Change Logo' : 'Upload Logo'}
                  <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Center - Code Editor */}
        <div className="flex-1 flex flex-col bg-gray-900">
          <div className="bg-gray-800 px-4 py-2 border-b border-gray-700">
            <span className="text-white font-mono text-sm">{activeFile}</span>
          </div>
          <div className="flex-1">
            <Editor
              height="100%"
              language={files[activeFile]?.language || 'plaintext'}
              value={files[activeFile]?.content || ''}
              onChange={handleEditorChange}
              theme="vs-dark"
              options={{
                minimap: { enabled: true },
                fontSize: 14,
                lineNumbers: 'on',
                roundedSelection: false,
                scrollBeyondLastLine: false,
                automaticLayout: true,
                tabSize: 2,
                wordWrap: 'on'
              }}
            />
          </div>
        </div>

        {/* Right - Live Preview (Fixed Large Width) */}
        <div className="w-[800px] bg-white border-l border-gray-200 flex flex-col shadow-lg">
          <div className="bg-gray-100 px-4 py-3 border-b border-gray-200 flex items-center gap-2">
            <FiEye className="text-gray-600" />
            <span className="font-medium text-gray-900">Live Preview</span>
            <div className="ml-auto flex gap-2">
              <button
                onClick={() => setPreviewMode('popup')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  previewMode === 'popup' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Popup
              </button>
              <button
                onClick={() => setPreviewMode('newtab')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  previewMode === 'newtab' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                New Tab
              </button>
            </div>
          </div>

          <div className="flex-1 bg-gray-50 flex items-center justify-center overflow-hidden">
            {previewMode === 'popup' ? (
              <div className="overflow-auto max-h-full p-6">
                <div style={{ width: '350px' }}>
                  <iframe
                    srcDoc={files['popup.html']?.content || ''}
                    className="w-full h-[550px] border-2 border-gray-300 rounded-lg bg-white shadow-xl"
                    title="Popup Preview"
                    sandbox="allow-scripts"
                  />
                </div>
              </div>
            ) : (
              <div className="w-full h-full p-6">
                <iframe
                  srcDoc={files['newtab.html']?.content || ''}
                  className="w-full h-full border-2 border-gray-300 rounded-lg bg-white shadow-xl"
                  title="New Tab Preview"
                  sandbox="allow-scripts"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default BuildExtension;
